import {
  ArrowPathIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  Alert,
  Badge,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  rem,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import { EmployerItem } from "../../models/employer.model";
import { EmployerDictionaryAdminService } from "../../services/admin";

export function OperationsCompaniesDictionary() {
  const [employers, setEmployers] = useState<EmployerItem[]>([]);
  const [filteredEmployers, setFilteredEmployers] = useState<EmployerItem[]>(
    []
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalEmployers, setTotalEmployers] = useState(0);

  const employerDictionaryService = new EmployerDictionaryAdminService();

  // Load data
  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await employerDictionaryService.getEmployersDictionary();
      setEmployers(data);
      setFilteredEmployers(data);
      setTotalEmployers(data.length);
    } catch (err) {
      console.error("Error loading employers:", err);
      setError(err instanceof Error ? err.message : "Failed to load employers");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filter employers based on search query
  useEffect(() => {
    if (!debouncedSearchQuery.trim()) {
      setFilteredEmployers(employers);
    } else {
      const filtered = employers.filter((employer) =>
        employer.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
      );
      setFilteredEmployers(filtered);
    }
  }, [debouncedSearchQuery, employers]);

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Breadcrumb
            items={[
              { label: "Operations Panel", href: "/operations" },
              { label: "Dictionaries", href: "/operations/dictionaries" },
              { label: "Companies & Employers", isActive: true },
            ]}
          />
          <Title order={1} size="h2" fw={700}>
            Companies & Employers Dictionary
          </Title>
          <Text c="dimmed" size="lg">
            View all companies and employers entered by users in the system
          </Text>
        </div>

        {/* Read-only notification */}
        <Alert
          icon={<InformationCircleIcon style={{ width: rem(20) }} />}
          title="Read-Only Dictionary"
          color="blue"
        >
          <Text size="sm">
            This is not a traditional dictionary but a collection of all
            companies and employers that users have entered in the system. It is
            designed to provide maximum flexibility in how employers can be
            named while still allowing searching and filtering.
          </Text>
          <Text size="sm" mt="sm">
            Since employers are created directly by users, this view is
            read-only. New entries will appear here automatically as they are
            added to user profiles.
          </Text>
        </Alert>

        {/* Search and Actions */}
        <Group justify="space-between" align="flex-end">
          <TextInput
            placeholder="Search employers..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.currentTarget.value)}
            leftSection={<MagnifyingGlassIcon style={{ width: rem(18) }} />}
            rightSection={
              searchQuery ? (
                <Button
                  variant="subtle"
                  size="compact-sm"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  <XMarkIcon style={{ width: rem(16) }} />
                </Button>
              ) : null
            }
            style={{ flex: 1, maxWidth: 400 }}
          />
          <Button
            leftSection={<ArrowPathIcon style={{ width: rem(16) }} />}
            onClick={loadData}
            variant="default"
            loading={isLoading}
          >
            Refresh
          </Button>
        </Group>

        {/* Statistics */}
        <Group>
          <Badge size="lg" variant="outline">
            Total: {totalEmployers}
          </Badge>
          {searchQuery && (
            <Badge size="lg" variant="outline" color="blue">
              Filtered: {filteredEmployers.length}
            </Badge>
          )}
        </Group>

        {/* Error state */}
        {error && (
          <Alert color="red" title="Error loading employers">
            {error}
            <Button onClick={loadData} mt="md" variant="outline" size="sm">
              Try Again
            </Button>
          </Alert>
        )}

        {/* Loading state */}
        {isLoading && (
          <Center py="xl">
            <Stack align="center">
              <Loader size="lg" />
              <Text>Loading employers...</Text>
            </Stack>
          </Center>
        )}

        {/* Employers table */}
        {!isLoading && !error && (
          <Paper withBorder>
            <ScrollArea>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: "80px" }}>Row No</Table.Th>
                    <Table.Th>Company/Employer Name</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredEmployers.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={2}>
                        <Center py="xl">
                          <Text c="dimmed">
                            {searchQuery
                              ? "No employers match your search"
                              : "No employers found in system"}
                          </Text>
                        </Center>
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    filteredEmployers.map((employer) => (
                      <Table.Tr key={employer.rowNo}>
                        <Table.Td>{employer.rowNo}</Table.Td>
                        <Table.Td>
                          <Text fw={500}>{employer.name}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Paper>
        )}

        {/* Help Card */}
        <Paper withBorder p="lg">
          <Stack gap="md">
            <Title order={3} size="h4">
              About Companies & Employers
            </Title>
            <Text size="sm">
              Companies and employers are entered directly by users when
              creating and editing their profiles or work experience. This
              approach provides maximum flexibility while still allowing
              standardized reporting.
            </Text>
            <Text size="sm">
              This view shows all unique company and employer names that have
              been entered in the system, which can help administrators
              understand the organizations represented across the user base.
            </Text>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}
